import { AccordionTypes, UI, HStack, Stack, VStack } from '@genial/components';
import { BoxBorder, Color, Spacing } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import React, { useState } from 'react';

export interface FundRulesProps {
  cutOffTime: string;
  minimumBalanceForPermanence: string;
  minimumMovementAmount: string;
  quotationAndSettlement: string;
  earlyRedemptionDescription: string;
  quotationDateFormatted: string;
  agreementChecked: boolean;
  agreementDisabled?: boolean;
  onCheckAgreement: (checked: boolean) => void;
}

export const FundRules: React.FC<FundRulesProps> = ({
  cutOffTime,
  minimumBalanceForPermanence,
  minimumMovementAmount,
  quotationAndSettlement,
  earlyRedemptionDescription,
  quotationDateFormatted,
  agreementChecked,
  agreementDisabled,
  onCheckAgreement,
}) => {
  const [accordionState, setAccordionState] = useState<AccordionTypes.Status>('closed');

  const handlePress = () => {
    setAccordionState((prevState) => (prevState === 'closed' ? 'opened' : 'closed'));
  };

  const handleClose = () => {
    setAccordionState('closed');
  };

  const handleCheckAgreement = (selected: boolean) => {
    onCheckAgreement(selected);
  };

  return (
    <Stack bgColor={Color.background} borderRadius={BoxBorder.heavy}>
      <UI.Accordion
        testId="customer-info-panel"
        status={accordionState}
        onPress={handlePress}
        hasShadow={false}
        onClosed={handleClose}>
        <UI.Accordion.Visible>
          <UI.Text variant="semibold">REGRAS DO FUNDO</UI.Text>
        </UI.Accordion.Visible>
        <UI.Accordion.Expanded>
          <VStack space={Spacing['stack.xxxs']}>
            <VStack space={Spacing['stack.nano']}>
              <Stack
                justifyContent="space-between"
                direction={Utils.Responsive.DesktopPhone({ desktop: 'row', phone: 'column' })}
                space={Spacing['stack.nano']}>
                <VStack space={Spacing['stack.quarck']} w="48%">
                  <UI.Text variant="semibold" color="neutral.black">
                    Horário de Corte
                  </UI.Text>
                  <UI.Text color="neutral.darkest">{cutOffTime}</UI.Text>
                </VStack>
                <VStack space={Spacing['stack.quarck']} w="48%">
                  <UI.Text variant="semibold" color="neutral.black">
                    Saldo mínimo de permanência
                  </UI.Text>
                  <UI.Text color="neutral.darkest">{minimumBalanceForPermanence}</UI.Text>
                </VStack>
              </Stack>
              <Stack
                justifyContent="space-between"
                direction={Utils.Responsive.DesktopPhone({ desktop: 'row', phone: 'column' })}
                space={Spacing['stack.nano']}>
                <VStack space={Spacing['stack.quarck']} w="48%">
                  <UI.Text variant="semibold" color="neutral.black">
                    Movimentação mínima
                  </UI.Text>
                  <UI.Text color="neutral.darkest">{minimumMovementAmount}</UI.Text>
                </VStack>
                <VStack space={Spacing['stack.quarck']} w="48%">
                  <UI.Text variant="semibold" color="neutral.black">
                    Cotização e Liquidação
                  </UI.Text>
                  <UI.Text color="neutral.darkest">{quotationAndSettlement}</UI.Text>
                </VStack>
              </Stack>
            </VStack>
            <VStack space={Spacing['stack.xxxs']}>
              <UI.Text variant="semibold">Resgate Antecipado: {earlyRedemptionDescription}</UI.Text>
              <VStack>
                <UI.Text>
                  (1) O valor líquido a ser creditado poderá ser inferior ao solicitado, pois está
                  sujeito a incidência de IR e/ou IOF.
                </UI.Text>
                <UI.Text>
                  (2) Os valores de IR e IOF são apenas uma estimativa com base na última cota
                  disponível, podendo sofrer alteração de acordo com o valor da cota na data de
                  cotização.
                </UI.Text>
                <UI.Text>Data de Cotização: {quotationDateFormatted}</UI.Text>
              </VStack>
            </VStack>
            <HStack space="inline.nano">
              <UI.Checkbox
                optionPadding={{ bottom: 0, left: 0, right: 0, top: 0 }}
                testId="fund-rules-agreement-checkbox"
                options={[{ label: '', value: 'agree', disabled: agreementDisabled }]}
                onChange={(_, selected) => handleCheckAgreement(selected)}
                selectedValues={agreementChecked ? ['agree'] : []}
              />
              <UI.Text>
                Estou de acordo com o cancelamento da simulação associada a esse portfolio.
              </UI.Text>
            </HStack>
          </VStack>
        </UI.Accordion.Expanded>
      </UI.Accordion>
    </Stack>
  );
};
